import { Box, Button, Menu, MenuItem, Skeleton, Stack, Typography } from '@mui/material'
import { useAccountId } from 'ContextProviders/CurrentAccount'
import { useEffect, useRef, useState } from 'react'
import { type CaseloadInfo } from 'core/types'
import { sendGet, sendPost } from 'hooks/use-fetch'
import { useRefresh, useRefreshDispatch } from 'ContextProviders/RefreshProvider'
import { CareManagementDisplay } from 'Components/CareManagement/CareManagementEdit'
import { usePermissions } from 'hooks/use-permissions'
import AddIcon from '@mui/icons-material/Add'
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd'
import { toast } from 'react-toastify'
import { ReadonlyCareManagementDisplay } from 'Components/CareManagement/ReadonlyCareManagementDisplay'

export function CareManagementSummary (props: { childID: number, childName: string, variant?: 'card' | 'chip' }): JSX.Element {
  const [loaded, setLoaded] = useState(false)
  const acct = useAccountId()
  const perms = usePermissions()
  const refreshTime = useRefresh()
  const disp = useRefreshDispatch()
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const refresh = (): void => { disp({ type: 'refresh' }) }
  const [caseloadInfo, setCaseloadInfo] = useState<CaseloadInfo | null>(null)
  const hasCareManagement = perms.CareNetwork_CareManagement
  const ref = useRef()

  function ComponentContainer (props: { variant: 'chip' | 'card', children: JSX.Element }): JSX.Element {
    if (props.variant === 'card') {
      return <Box className='CardDisplay'>
        {props.children}
      </Box>
    } else {
      return <>{props.children}</>
    }
  }

  function DetailDisplayContainer (props: { variant: 'chip' | 'card', children: JSX.Element }): JSX.Element {
    if (props.variant === 'chip') {
      return <>{props.children}</>
    } else {
      return <Stack direction='column' spacing={1}>
        {props.children}
      </Stack>
    }
  }
  useEffect(() => {
    const getData = async (): Promise<void> => {
      const retval = await sendGet(`/CareManagement/GetCareManagement?childId=${props.childID}`, {})
      setCaseloadInfo(retval.response)
      setLoaded(true)
    }
    void getData()
  }, [props.childID, acct?.id, refreshTime])

  const AssignToIntake = async (providerId: number): Promise<void> => {
    const { response: result } = await sendPost('/CareManagement/AssignToIntake', { childId: props.childID, assignedOrgID: providerId })
    if (result.errors.length === 0) {
      refresh()
      toast.success(result.message)
      setAnchorEl(null)
    }
  }
  const handleClick = async (event: React.MouseEvent<HTMLButtonElement>): Promise<void> => {
    if (caseloadInfo?.availableIntakes?.length === 1) {
      await AssignToIntake(caseloadInfo.availableIntakes[0].id)
    } else if ((caseloadInfo?.availableIntakes?.length ?? 0) > 1) {
      setAnchorEl(event.currentTarget)
    }
  }
  const closeMenu = (): void => {
    setAnchorEl(null)
  }
  const handleIntakeSelect = async (id: number): Promise<void> => {
    await AssignToIntake(id)
  }

  const phone = caseloadInfo?.userPhone ?? caseloadInfo?.orgPhone
  const email = caseloadInfo?.userEmail ?? caseloadInfo?.orgEmail

  return <Box ref={ref}>
    {!loaded && (
      <Skeleton variant="rectangular" width={'auto'} height={props.variant === 'chip' ? 100 : 200} />
    )}
    {loaded && (<>
      <ComponentContainer variant={props.variant ?? 'card'}>
        <>
          {props.variant !== 'chip' && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', flexDirection: 'row' }}>
              <Typography variant='h6' sx={{ fontWeight: 'bold' }} gutterBottom>Care Management</Typography>
              {hasCareManagement && <CareManagementDisplay childId={props.childID} childName={props.childName} caseloadInfo={caseloadInfo} />}
            </Box>
          )}
          <DetailDisplayContainer variant={props.variant ?? 'card'}>
            <>
              {caseloadInfo?.orgId == null && (
                <>
                  {props.variant !== 'chip' && <Stack direction='column' spacing={2} alignItems='center'>
                    <AssignmentIndIcon color='secondary' opacity={'60%'} sx={{ height: '60px', width: '60px' }} />
                    <Typography variant='body1' sx={{ textAlign: 'center' }}>No Care Management is assigned{caseloadInfo?.isdName != null ? ` for ${caseloadInfo?.isdName}` : ''}</Typography>
                    {(caseloadInfo?.availableIntakes?.length ?? 0) > 0 && (perms.Enrollment_Edit || hasCareManagement) && (
                      <Button fullWidth variant={'contained'}
                        color={'primary'}
                        sx={{ textTransform: 'none' }} onClick={handleClick}
                      >
                        Assign to Care Management
                      </Button>
                    )}
                  </Stack>}
                  {props.variant === 'chip' && (caseloadInfo?.availableIntakes?.length ?? 0) > 0 && (perms.Enrollment_Edit || hasCareManagement) && (<>
                    <Button variant={'text'}
                      startIcon={ <AddIcon /> } color={ 'inherit' } sx={{ textTransform: 'none' }} onClick={handleClick}
                    >
                      Add Intake
                    </Button>

                  </>)}
                  <Menu
                    open={anchorEl != null}
                    anchorEl={ref.current}
                    onClose={closeMenu}
                  >
                    {caseloadInfo?.availableIntakes?.map(i => (
                      <MenuItem key={i.name} onClick={async () => { await handleIntakeSelect(i.id) }}>{i.name}</MenuItem>
                    ))}
                  </Menu>
                </>)}
              {caseloadInfo?.orgId != null && (<>
                <Box display={'flex'} sx={{ gap: props.variant === 'chip' ? 2 : 0 }}>
                  {props.variant === 'chip' && <Typography sx={{ alignSelf: 'center' }}>
                    Intake Assigned
                  </Typography>}
                  <ReadonlyCareManagementDisplay
                    email={email ?? null}
                    orgName={caseloadInfo.orgName ?? null}
                    phone={phone ?? null}
                    userName={caseloadInfo.userName ?? null}
                    website={caseloadInfo.orgEmail ?? null}
                  />
                </Box>
              </>)}
            </>
          </DetailDisplayContainer>
        </>
      </ComponentContainer>
    </>)}
  </Box>
}
